import { fade, makeStyles } from '@material-ui/core/styles';
import { colors } from '@material-ui/core';

export const useStyles = makeStyles((theme) => ({
  menuWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  container: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
    paddingLeft: '10px',
    paddingRight: '10px',
    '& .MuiListItemIcon-root': {
      minWidth: 28,
      color: colors.grey[500],
    },
    '& .MuiListItemIcon-root .MuiSvgIcon-root': {
      fontSize: 20,
    },
    '& .MuiListItemText-root span': {
      fontSize: 13,
    },
    '& .MuiListItem-root': {
      marginTop: 4,
      marginBottom: 4,
      boxSizing: 'border-box',
    },
    // Expanded: inset pill with left-edge accent on selected
    '& .MuiListItem-root:not($railItem)': {
      borderRadius: 8,
      boxSizing: 'border-box',
    },
    '& .MuiListItem-root:not($railItem):hover:not(.Mui-selected):not($groupHeaderActive) .MuiListItemIcon-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiListItem-root:not($railItem):hover:not(.Mui-selected):not($groupHeaderActive) .MuiListItemText-root span': {
      fontWeight: 500,
    },
    '& .MuiListItem-root:not($railItem).Mui-selected': {
      borderRadius: 8,
      backgroundColor: fade(theme.palette.primary.main, 0.1),
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 3,
        top: '20%',
        bottom: '20%',
        width: 3,
        borderRadius: 3,
        backgroundColor: theme.palette.primary.main,
      },
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.main,
        opacity: 1,
      },
      '& .MuiListItemText-root span': {
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
    },
    // Rail: selected tile gets accent background, no left border
    '& $railItem.Mui-selected': {
      borderLeft: 'none',
      backgroundColor: fade(theme.palette.primary.main, 0.1),
      borderRadius: 8,
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.main,
        opacity: 1,
      },
      '& $railLabel': {
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
    },
  },
  // Sub-item list indented with left track line
  subList: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    marginLeft: 22,
    marginBottom: 8,
    paddingLeft: 12,
    '& .MuiListItemIcon-root .MuiSvgIcon-root': {
      fontSize: 17,
    },
    '& .MuiListItemText-root span': {
      fontSize: 12,
    },
  },
  // Nested sub-group list, indented one level deeper than subList
  subSubList: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    marginLeft: 22,
    marginBottom: 4,
    paddingLeft: 12,
    '& .MuiListItemIcon-root .MuiSvgIcon-root': {
      fontSize: 16,
    },
    '& .MuiListItemText-root span': {
      fontSize: 12,
    },
  },
  // Sizes the Mambu brand-mark svg to match sibling menu icons at this nesting depth.
  // No explicit color: it must inherit from .MuiListItemIcon-root so selected/hover/active
  // tinting (which sets color on that parent) still reaches this icon.
  mambuIcon: {
    width: 16,
    height: 16,
  },
  groupHeader: {
    paddingRight: 8,
    paddingLeft: 12,
  },
  menuItem: {
    paddingRight: 8,
    paddingLeft: 12,
    '&:hover $notLicensedChip': {
      backgroundColor: fade(theme.palette.warning.main, 0.12),
      borderColor: theme.palette.warning.main,
      '& .MuiChip-icon': {
        display: 'none',
      },
      '& .MuiChip-label': {
        maxWidth: 60,
        padding: '0 8px',
        opacity: 1,
      },
    },
  },
  // Group header (top-level or nested) when a child route is active
  groupHeaderActive: {
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
      opacity: 1,
    },
    '& .MuiListItemText-root span': {
      fontWeight: 500,
    },
  },
  // Sub-group header inside a rail flyout when a child route is active — same
  // subtle icon/text tint as groupHeaderActive, deliberately without the filled
  // background pill flyoutItem's .Mui-selected applies to real navigable leaf items.
  flyoutHeaderActive: {
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
      opacity: 1,
    },
    '& .MuiListItemText-root span': {
      fontWeight: 500,
    },
  },
  // ── Rail (collapsed) tile styles ──
  railItem: {
    flexDirection: 'column',
    height: 'auto',
    padding: '6px 0',
    marginLeft: '0 !important',
    marginRight: '0 !important',
    width: '100% !important',
    borderRadius: '8px !important',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover:not(.Mui-selected):not($railGroupActive) $railIcon': {
      color: theme.palette.text.primary,
    },
    '&:hover:not(.Mui-selected):not($railGroupActive) $railLabel': {
      fontWeight: 500,
    },
  },
  railIcon: {
    minWidth: 'unset',
    width: '100%',
    justifyContent: 'center',
    marginBottom: 1,
    color: colors.grey[500],
    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  },
  railLabel: {
    fontSize: 10,
    fontWeight: 400,
    textAlign: 'center',
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 82,
    color: theme.palette.text.primary,
  },
  railGroupActive: {
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
      opacity: 1,
    },
    '& $railLabel': {
      fontWeight: 600,
    },
  },
  // ── Flyout panel ──
  flyoutPaper: {
    minWidth: 220,
    boxShadow: '0 2px 10px rgba(16,22,40,0.16)',
    marginLeft: 0,
    padding: '4px 8px 8px',
  },
  flyoutItem: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 12,
    paddingRight: 12,
    marginBottom: 2,
    borderRadius: 8,
    '& .MuiListItemText-root span': {
      fontSize: 12,
      color: theme.palette.text.primary,
    },
    '&:hover:not(.Mui-selected):not($flyoutHeaderActive) $flyoutIcon': {
      color: theme.palette.text.primary,
    },
    '&:hover:not(.Mui-selected):not($flyoutHeaderActive) .MuiListItemText-root span': {
      fontWeight: 500,
    },
    '&:hover $notLicensedChip': {
      backgroundColor: fade(theme.palette.warning.main, 0.12),
      borderColor: theme.palette.warning.main,
      '& .MuiChip-icon': {
        display: 'none',
      },
      '& .MuiChip-label': {
        maxWidth: 60,
        padding: '0 8px',
        opacity: 1,
      },
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: fade(theme.palette.primary.main, 0.1),
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.main,
        opacity: 1,
      },
      '& .MuiListItemText-root span': {
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
    },
  },
  flyoutIcon: {
    minWidth: 32,
    color: colors.grey[500],
    '& .MuiSvgIcon-root': {
      fontSize: 17,
    },
  },
  flyoutTitle: {
    padding: '8px 8px 4px',
    marginBottom: 8,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: colors.grey[500],
  },
  newChip: {
    marginLeft: 8,
    height: 20,
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '& .MuiChip-label': {
      padding: '0 7px',
    },
  },
  lockIcon: {
    color: theme.palette.text.disabled,
    fontSize: 16,
  },
  notLicensedChip: {
    marginLeft: 8,
    height: 20,
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: 'transparent',
    color: theme.palette.warning.main,
    border: '1px solid transparent',
    transition: 'background-color 200ms ease, border-color 200ms ease',
    '& .MuiChip-icon': {
      color: theme.palette.warning.main,
      margin: '0 !important',
    },
    '& .MuiChip-label': {
      maxWidth: 0,
      padding: 0,
      opacity: 0,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      transition: 'max-width 200ms ease, opacity 200ms ease, padding 200ms ease',
    },
  },
  // ── Footer ──
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: '10px 16px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
  footerLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    textDecoration: 'none',
    color: 'inherit',
    opacity: 0.42,
    transition: 'opacity 200ms ease',
    '&:hover': {
      opacity: 1,
    },
  },
  footerLogoRail: {
    width: '100%',
    justifyContent: 'center',
  },
  footerLogoIcon: {
    width: 40,
    height: 40,
    flexShrink: 0,
  },
  footerLogoFull: {
    height: 20,
    width: 'auto',
  },
  footerText: {
    fontSize: 11,
    color: colors.grey[500],
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
  footerTextLink: {
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  expandIcon: {
    color: theme.palette.text.secondary,
    fontSize: 20,
  },
  divider: {
    marginTop: '12px !important',
    marginBottom: '12px !important',
  },
}));
